import React, { useState, useEffect } from 'react';
import { addTile, getSizes } from '../../services/api';
import colors from '../../theme/colors';

export default function AddTileModal({ isOpen, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    product_size_id: '',
    application_type: 'both',
    surface_type: '',
    price: '',
    sku: '',
    pieces_per_box: '',
    price_unit: 'Box',
    coverage_area_per_box: '',
    coverage_area_unit: 'Per Sqmr',
    weight_per_box: '',
    weight_unit: 'Kg',
    thickness_mm: '',
    images: [],
    packing_details: ''
  });

  const [sizes, setSizes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [addPackingDetails, setAddPackingDetails] = useState(false);
  const [descriptionLength, setDescriptionLength] = useState(950);

  const handleGenerateSKU = () => {
    const generated = 'SKU-' + Math.floor(100000 + Math.random() * 900000);
    setFormData((prev) => ({ ...prev, sku: generated }));
  };

  const handleDescriptionChange = (e) => {
    const text = e.target.value;
    if (text.length <= 950) {
      handleChange(e);
      setDescriptionLength(950 - text.length);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchSizes();
    }
  }, [isOpen]);

  const fetchSizes = async () => {
    const token = localStorage.getItem('retailer_token');
    
    try {
      const response = await getSizes();
      if (response.success) {
        setSizes(response.sizes);
      }
    } catch (err) {
      console.error('Failed to fetch sizes:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'images' ? files : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const token = localStorage.getItem('retailer_token');
    

    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (key === 'images') {
          for (let i = 0; i < formData.images.length; i++) {
            formDataToSend.append('images', formData.images[i]);
          }
        } else if (key === 'packing_details' && !addPackingDetails) {
          // Skip packing details if not enabled
          return;
        } else if (formData[key] !== '' && formData[key] !== null) {
          formDataToSend.append(key, formData[key]);
        }
      });

      const response = await addTile(formDataToSend);
      if (response.success) {
        setFormData({
          product_size_id: '',
          application_type: 'both',
          surface_type: '',
          price: '',
          sku: '',
          pieces_per_box: '',
          price_unit: 'Box',
          coverage_area_per_box: '',
          coverage_area_unit: 'Per Sqmr',
          weight_per_box: '',
          weight_unit: 'Kg',
          thickness_mm: '',
          images: [],
          packing_details: ''
        });
        setAddPackingDetails(false);
        onSuccess && onSuccess();
        onClose();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-5xl w-full max-h-[90vh] flex flex-col shadow-2xl overflow-hidden font-sans">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-white">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-bold text-gray-800">Add a New Product Design SKU</h2>
            <span className="text-sm text-gray-500 font-normal">
              (You can add up to <strong className="text-gray-700">0</strong> New Product Designs)
            </span>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto flex-1 space-y-6">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              {error}
            </div>
          )}

          <form id="product-form" onSubmit={handleSubmit} className="space-y-6">
            
            {/* Top Row Inputs */}
            <div className="grid grid-cols-4 gap-4">
              {/* Select Product Size */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Select Product Size <span className="text-red-500">*</span>
                </label>
                <select
                  name="product_size_id"
                  value={formData.product_size_id || ''}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none bg-white"
                >
                  <option value="">Select Size</option>
                  {sizes.map((size) => (
                    <option key={size.id} value={size.id}>
                      {size.name} ({size.width}x{size.height} {size.unit})
                    </option>
                  ))}
                </select>
              </div>

              {/* Application Type */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Application Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="application_type"
                  value={formData.application_type || ''}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none bg-white"
                >
                  <option value="">Select Application</option>
                  <option value="wall">Wall</option>
                  <option value="floor">Floor</option>
                  <option value="both">Both</option>
                </select>
              </div>

              {/* Surface Type */}
              {/* Company Name */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Company Name
                </label>
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name || ''}
                  onChange={handleChange}
                  placeholder="Ex: Kajaria"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none bg-white"
                />
              </div>

              {/* Surface Type */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Surface Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="surface_type"
                  value={formData.surface_type || ''}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none bg-white"
                >
                  <option value="">Select Surface</option>
                  <option value="glossy">Glossy</option>
                  <option value="matte">Matte</option>
                  <option value="carved">Carved</option>
                </select>
              </div>

              {/* SKU / Product Code with Generate Button */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  SKU / Product Code
                </label>
                <div className="flex rounded-md shadow-sm">
                  <input
                    type="text"
                    name="sku"
                    value={formData.sku || ''}
                    onChange={handleChange}
                    placeholder="Enter Product Code"
                    className="flex-1 min-w-0 px-3 py-2 text-sm border border-r-0 border-gray-300 rounded-l-md focus:ring-1 focus:ring-indigo-500 outline-none"
                  />
                  <button
                    type="button"
                    onClick={handleGenerateSKU}
                    className="px-3 py-2 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-r-md transition-colors"
                  >
                    Generate
                  </button>
                </div>
              </div>
            </div>

            {/* Checkbox Toggle for Packaging Details */}
            <div className="border-t border-b border-gray-100 py-3">
              <label className="inline-flex items-center cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={addPackingDetails}
                  onChange={(e) => setAddPackingDetails(e.target.checked)}
                  className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
                <span className="ml-2 text-xs font-semibold text-gray-600">
                  Do you want to add packing details for this product?
                </span>
              </label>
            </div>

            {/* Conditionally Rendered Packaging Details */}
            {addPackingDetails && (
              <div className="space-y-4 bg-gray-50/50 p-4 rounded-lg border border-gray-100">
                <div className="grid grid-cols-4 gap-4">
                  {/* Enter Price */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs font-semibold text-gray-600">Enter Price</label>
                      <label className="inline-flex items-center text-xs text-gray-500 cursor-pointer">
                        <input
                          type="checkbox"
                          name="sell_by_box"
                          checked={formData.sell_by_box || false}
                          onChange={handleChange}
                          className="w-3.5 h-3.5 text-indigo-600 rounded mr-1"
                        />
                        Sell by Box?
                      </label>
                    </div>
                    <div className="flex rounded-md">
                      <input
                        type="number"
                        step="0.01"
                        name="price"
                        value={formData.price || ''}
                        onChange={handleChange}
                        placeholder="Enter Price"
                        className="flex-1 min-w-0 px-3 py-2 text-sm border border-r-0 border-gray-300 rounded-l-md outline-none"
                      />
                      <select
                        name="price_unit"
                        value={formData.price_unit || 'Per Box'}
                        onChange={handleChange}
                        className="w-28 px-2 py-2 text-xs border border-gray-300 rounded-r-md bg-white outline-none"
                      >
                        <option value="Per Box">Per Box</option>
                        <option value="Per Sqft">Per Sqft</option>
                        <option value="Per Sqmr">Per Sqmr</option>
                        <option value="Per Piece">Per Piece</option>
                      </select>
                    </div>
                  </div>

                  {/* Piece Per Box */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Piece Per Box</label>
                    <input
                      type="number"
                      name="pieces_per_box"
                      value={formData.pieces_per_box || ''}
                      onChange={handleChange}
                      placeholder="Piece Per Box"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md outline-none"
                    />
                  </div>

                  {/* Coverage Area Per Box */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Coverage Area Per Box</label>
                    <div className="flex rounded-md">
                      <input
                        type="number"
                        step="0.01"
                        name="coverage_area_per_box"
                        value={formData.coverage_area_per_box || ''}
                        onChange={handleChange}
                        placeholder="Coverage Area"
                        className="flex-1 min-w-0 px-3 py-2 text-sm border border-r-0 border-gray-300 rounded-l-md outline-none"
                      />
                      <select
                        name="coverage_area_unit"
                        value={formData.coverage_area_unit || 'Per Sqmr'}
                        onChange={handleChange}
                        className="w-28 px-2 py-2 text-xs border border-gray-300 rounded-r-md bg-white outline-none"
                      >
                        <option value="Per Sqmr">Per Sqmr</option>
                        <option value="Per Sqft">Per Sqft</option>
                      </select>
                    </div>
                  </div>

                  {/* Weight Per Box */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Weight Per Box</label>
                    <div className="flex rounded-md">
                      <input
                        type="number"
                        step="0.01"
                        name="weight_per_box"
                        value={formData.weight_per_box || ''}
                        onChange={handleChange}
                        placeholder="Weight Per Box"
                        className="flex-1 min-w-0 px-3 py-2 text-sm border border-r-0 border-gray-300 rounded-l-md outline-none"
                      />
                      <select
                        name="weight_unit"
                        value={formData.weight_unit || 'Kg'}
                        onChange={handleChange}
                        className="w-20 px-2 py-2 text-xs border border-gray-300 rounded-r-md bg-white outline-none"
                      >
                        <option value="Kg">Kg</option>
                        <option value="Lb">Lb</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-4">
                  {/* Thickness */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Thickness</label>
                    <div className="flex rounded-md">
                      <input
                        type="number"
                        step="0.1"
                        name="thickness_mm"
                        value={formData.thickness_mm || ''}
                        onChange={handleChange}
                        placeholder="Thickness"
                        className="flex-1 min-w-0 px-3 py-2 text-sm border border-r-0 border-gray-300 rounded-l-md outline-none"
                      />
                      <select
                        name="thickness_unit"
                        value={formData.thickness_unit || 'MM'}
                        onChange={handleChange}
                        className="w-20 px-2 py-2 text-xs border border-gray-300 rounded-r-md bg-white outline-none"
                      >
                        <option value="MM">MM</option>
                        <option value="IN">IN</option>
                      </select>
                    </div>
                  </div>

                  {/* Stock */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Stock</label>
                    <div className="flex rounded-md">
                      <input
                        type="number"
                        name="stock"
                        value={formData.stock || ''}
                        onChange={handleChange}
                        placeholder="Stock"
                        className="flex-1 min-w-0 px-3 py-2 text-sm border border-r-0 border-gray-300 rounded-l-md outline-none"
                      />
                      <select
                        name="stock_unit"
                        value={formData.stock_unit || 'Box'}
                        onChange={handleChange}
                        className="w-20 px-2 py-2 text-xs border border-gray-300 rounded-r-md bg-white outline-none"
                      >
                        <option value="Box">Box</option>
                        <option value="Piece">Piece</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Description & Default Layout */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Product Description</label>
                    <textarea
                      name="description"
                      value={formData.description || ''}
                      onChange={handleDescriptionChange}
                      rows={3}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md outline-none resize-none"
                    />
                    <p className="text-[11px] text-gray-400 mt-1">{descriptionLength} characters remaining</p>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Default Layout</label>
                    <select
                      name="default_layout"
                      value={formData.default_layout || ''}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white outline-none text-gray-600"
                    >
                      <option value="">-- Select Default Layout --</option>
                      <option value="horizontal">Horizontal</option>
                      <option value="vertical">Vertical</option>
                      <option value="grid">Grid</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Design File Upload Drag and Drop Section */}
            <div className="border border-slate-300 rounded-lg overflow-hidden">
              {/* Header inside Upload Box */}
              <div className="bg-[#2D4356] px-4 py-2.5 flex items-center justify-between text-white">
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider">Upload Your Design Files</h4>
                  <p className="text-[11px] text-gray-300">Drag & Drop Files Here OR Click 'Add Files'</p>
                </div>
                <div className="flex gap-1 border border-slate-500 rounded p-0.5 bg-slate-700/50">
                  <button type="button" className="p-1 hover:bg-slate-600 rounded">
                    <svg className="w-3.5 h-3.5 text-gray-200" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                  </button>
                  <button type="button" className="p-1 hover:bg-slate-600 rounded">
                    <svg className="w-3.5 h-3.5 text-gray-200" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Upload Drag Area */}
              <div className="relative min-h-[160px] bg-white flex items-center justify-center p-6 border-b border-gray-200">
                <input
                  type="file"
                  name="images"
                  onChange={handleChange}
                  multiple
                  accept="image/*"
                  required
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                />
                <span className="text-xs text-gray-500 font-medium">Drag files here.</span>
              </div>

              {/* Upload Status / Action Bar */}
              <div className="bg-slate-50 px-4 py-3 flex items-center justify-between">
                <label className="relative cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded bg-white hover:bg-gray-50 text-xs font-semibold text-gray-700 shadow-sm">
                  <span className="text-base leading-none text-gray-500">+</span> Add Files
                  <input
                    type="file"
                    name="images"
                    onChange={handleChange}
                    multiple
                    accept="image/*"
                    className="hidden"
                  />
                </label>
                <div className="flex gap-4 text-xs text-gray-600 font-bold">
                  <span>0%</span>
                  <span>0 kb</span>
                </div>
              </div>
            </div>

          </form>
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
          <a
            href="#help"
            onClick={(e) => e.preventDefault()}
            className="text-xs font-semibold text-indigo-600 hover:underline flex items-center gap-1"
          >
            How to Add Product Design SKU
            <span className="inline-block w-3.5 h-3.5 text-center leading-3 rounded-full bg-indigo-600 text-white text-[10px]">?</span>
          </a>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-semibold text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="product-form"
              disabled={loading}
              className="px-5 py-2 text-xs font-semibold text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 transition-colors shadow-sm"
            >
              {loading ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
